import { Outlet } from 'react-router-dom'

import {
  Box,
  Button,
  Icon,
  SearchField,
  Spinner,
  Tag,
  TagColor,
  Text,
  ToggleButton,
} from '@factorialco/deprecated-design-system'
import {
  ApplicationsDataTableQuery,
  ProjectionKind,
  useApplicationsDataTable,
} from 'generated/resources/datatable.graphql'
import { JobPostingDocument } from 'generated/resources/graphql'
import { capitalize, map } from 'lodash'

import { observer } from 'mobx-react'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import { type TabType, Tabs } from 'modules/core/components/Table'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ApplicationPhaseTag } from 'modules/ats/components/ApplicationPhaseTag'
import { ApplicationTopMatchTag } from 'modules/ats/components/ApplicationTopMatchTag'
import { AssessmentStatusTag } from 'modules/ats/components/AssessmentStatusTag'
import { OfferLetterStatusTag } from 'modules/ats/components/OfferLetterStatusTag'
import { ScoreRating } from 'modules/ats/components/ScoreRating'
import { TagsSelector } from 'modules/ats/components/TagsSelector/TagsSelector'
import { sortApplicationPhases } from 'modules/ats/lib/utils/applicationPhase'
import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'

import { AnswersFilter } from './AnswersFilter'
import { ApplicationTableBulkOptions } from './ApplicationTableBulkActions'
import { type ApplicationFilters, useApplicationsFilters } from './useApplicationsFilters'

interface Props {
  jobId: string
}

export const ApplicationsTable = observer(({ jobId }: Props) => {
  const { navigation } = useNavigation()
  const { filters, onFiltersChange, search, onSearch } = useApplicationsFilters(jobId)
  const { access, company } = useCurrentSession()

  const { data, loading } = useQuery(JobPostingDocument, {
    variables: { atsJobPostingId: jobId, accessId: access.id, companyId: company.id },
  })

  const jobPosting = data?.ats.jobPosting
  const canManageJobPostings = data?.ats._policies.manageJobPostings.can
  const hiringRole = jobPosting?.currentHiringRole[0]?.hiringRole ?? 'editor'
  const isReviewerRole = hiringRole === 'reviewer' && !canManageJobPostings

  if (loading || !jobPosting) {
    return (
      <Box padding="s40" width="full" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  const orderedApplicationPhases = sortApplicationPhases(jobPosting.applicationPhases)

  const rejectedApplicationsCount =
    jobPosting.applicationsCount - jobPosting.activeApplicationsCount

  const tabs: Array<TabType<ApplicationFilters['status']>> = [
    {
      id: 'active',
      counter: jobPosting?.activeApplicationsCount,
      label: i18n.t('ats.job_details.applications.tabs.active'),
      color: TagColor.viridian,
    },
    {
      id: 'rejected',
      counter: rejectedApplicationsCount,
      label: i18n.t('ats.job_details.applications.tabs.rejected'),
      color: TagColor.viridian,
    },
  ]

  const hasOfferLetterFeature =
    data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_offer_letter')
      ?.enabled ?? false
  const hasAssessmentFeature =
    data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_ideal_disc_integration')
      ?.enabled ?? false
  const hasDuplicationServiceFeature =
    data?.features.enabledFeatures.nodes.find(
      (node) => node.name === 'dev_ats_duplication_identification_service'
    )?.enabled ?? false

  const source = useApplicationsDataTable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.job_details.applications.table_headers.name'),
        template: '{{ candidate.fullName }}',
        sortBy: 'fullName',
        render: (application) => <Text.Normal>{application.candidate.fullName}</Text.Normal>,
        colSpan: 2,
        fixed: true,
      },
      {
        identifier: 'phase',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.job_details.applications.table_headers.phase'),
        template: '{{ applicationPhase.name }}',
        sortBy: 'atsApplicationPhasePosition',
        render: (application) => {
          const hasOfferLetter = hasOfferLetterFeature && !!application.offerLetter

          return (
            <Box gap="s8" alignItems="center" flexDirection="row">
              <ApplicationPhaseTag applicationPhase={application.applicationPhase} />

              {hasOfferLetter && (
                <OfferLetterStatusTag status={application.offerLetter?.status ?? ''} />
              )}
              {hasAssessmentFeature && application.assessment && !hasOfferLetter && (
                <AssessmentStatusTag status={application.assessment.status} />
              )}
            </Box>
          )
        },
        colSpan: 4,
      },
      {
        identifier: 'createdAt',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.job_details.applications.table_headers.added_on'),
        template: '{{ createdAt }}',
        sortBy: 'createdAt',
        render: (application) => (
          <>
            <Text.Normal>{dateBackendDateTimeToUI(application.createdAt)}</Text.Normal>
          </>
        ),
        colSpan: 2,
      },
      {
        identifier: 'source',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.job_details.applications.table_headers.source'),
        template: '{{medium}} {{ source }}',
        sortBy: 'atsCandidateSourceName',
        render: (application) => {
          const isAISuggestion = application.applicationSource?.name == 'ai_suggestion'
          const icon = isAISuggestion ? <Icon.Small icon="ai" color="lavander1000" /> : null
          return (
            <>
              {application.applicationSource?.name && application.medium ? (
                <Box gap="s4">
                  <Text.Normal weight="medium">{capitalize(application.medium)}</Text.Normal>
                  <Box flexDirection="row" gap="s8" alignItems="center">
                    {icon}
                    <Text.Normal size="200" color="grey800">
                      {getSourceLabel(application.applicationSource.name)}
                    </Text.Normal>
                  </Box>
                </Box>
              ) : (
                <Box flexDirection="row" gap="s8" alignItems="center">
                  {icon}
                  <Text.Normal weight="medium">
                    {getSourceLabel(application.applicationSource?.name)}
                  </Text.Normal>
                </Box>
              )}
            </>
          )
        },
        colSpan: 2,
      },
      ...(hasDuplicationServiceFeature
        ? [
            {
              identifier: 'alerts',
              kind: ProjectionKind.Custom,
              title: i18n.t('ats.job_details.applications.table_headers.alerts'),
              template: '{{ alerts }}',
              render: (
                application: ApplicationsDataTableQuery['ats']['applicationsConnection']['edges'][number]['node']
              ) => {
                const hasDuplicates =
                  (application.candidate?.duplicateIdentificationsAsMatched?.length ?? 0) > 0 ||
                  (application.candidate?.duplicateIdentificationsAsSource?.length ?? 0) > 0

                if (!hasDuplicates) {
                  return null
                }

                return (
                  <>
                    <Tag type={Tag.Types.tangerine} leftItem="icon" icon="alert-circle">
                      {i18n.t('ats.candidates.list.filters.alerts_options.duplicate')}
                    </Tag>
                  </>
                )
              },
              colSpan: 1,
            },
          ]
        : []),
      {
        identifier: 'rating',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.job_details.applications.table_headers.rating'),
        template: '{{ talentPool }}',
        sortBy: 'ratingAverage',
        render: (application) => {
          const showTopMatch =
            application.summary?.topMatch &&
            application.openAiFeedbackTopMatchRating?.helpful !== false

          return (
            <Box gap="s4">
              <Box gap="s12" flexDirection="row">
                <ScoreRating rating={application.ratingAverage} showEmpty={!showTopMatch} />
                {showTopMatch && <ApplicationTopMatchTag type="viridian" />}
              </Box>
              <Text.Normal size="200" color="grey800">
                {!application.qualified && application.rejectionReason?.reason}
              </Text.Normal>
            </Box>
          )
        },
        colSpan: 3,
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: (application) => (
          <Button.Router
            type="secondary"
            aria-label={i18n.t('ats.candidates.candidate_details', {
              candidate_name: application.candidate?.fullName,
            })}
            to={navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
              jobId,
              application_id: application.id,
            })}
            iconName="arrow-right"
          />
        ),
        colSpan: 1,
      },
    ],
    {
      variables: {
        atsJobPostingId: jobId,
        atsTagIds: filters.tags,
        atsApplicationPhaseId:
          filters.applicationPhaseId !== '-1' ? filters.applicationPhaseId : undefined,
        qualified: filters.status === 'active',
        search,
        atsAnswers: map(filters.answers, (value, key) => ({
          questionId: String(key),
          value,
        })),
      },
    }
  )

  return (
    <Box width="full">
      <Tabs
        tabs={tabs}
        selectedTab={tabs.find((t) => t.id === filters.status)}
        onClick={(selected) => onFiltersChange({ status: selected.id })}
      >
        {() => null}
      </Tabs>

      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 100,
        }}
        sort={{
          path: 'createdAt',
          direction: 'desc',
        }}
        rowTo={(application) =>
          navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
            jobId,
            application_id: application.id,
          })
        }
      >
        <Box
          flexGrow
          overflowX="hidden"
          background="grey200"
          padding="s16"
          border={{
            all: { style: 'solid', color: 'grey400', width: 's1' },
            bottom: { style: 'hidden', color: 'grey400', width: 's1' },
          }}
        >
          <Box
            flexDirection="row"
            gap="s8"
            alignItems="center"
            width="full"
            overflowX="auto"
            paddingBottom="s16"
            paddingRight="s4"
          >
            <div style={{ flexShrink: 0 }}>
              <ToggleButton
                toggled={filters.applicationPhaseId === '-1'}
                onClick={() => onFiltersChange({ applicationPhaseId: '-1' })}
                label={i18n.t('ats.applications_table.all_phase', {
                  amount:
                    filters.status === 'active'
                      ? jobPosting.activeApplicationsCount
                      : jobPosting.applicationsCount - jobPosting.activeApplicationsCount,
                })}
              />
            </div>
            {orderedApplicationPhases.map((ap) => {
              const hasCounts = ap.applicationsCount !== null && ap.activeApplicationsCount !== null
              const applicationsCount = hasCounts
                ? filters.status === 'active'
                  ? ap.activeApplicationsCount
                  : ap.applicationsCount! - ap.activeApplicationsCount!
                : null

              return (
                <div key={ap.id} style={{ flexShrink: 0 }}>
                  <ToggleButton
                    toggled={filters.applicationPhaseId === ap.id}
                    onClick={() => onFiltersChange({ applicationPhaseId: ap.id })}
                    label={ap.name + (hasCounts ? ` (${applicationsCount})` : '')}
                  />
                </div>
              )
            })}
          </Box>

          <Box flexDirection="row" gap="s8" alignItems="center">
            <Box width="full" flexDirection="row" gap="s8">
              <Box width="s140">
                <TagsSelector
                  value={filters.tags}
                  onChange={(tags) => {
                    onFiltersChange({ tags })
                    track(trackingEvents.ats.filterApplicationsByTag)
                  }}
                />
              </Box>
              <AnswersFilter
                value={filters.answers}
                onChange={(answers: Record<string, string>) => {
                  onFiltersChange({ answers })
                }}
                jobId={jobId}
              />
              <Box flexGrow>
                <SearchField
                  variant="big"
                  search={search}
                  onSearch={onSearch}
                  placeholder={i18n.t('common.search_by')}
                />
              </Box>
            </Box>

            {!isReviewerRole && (
              <ApplicationTableBulkOptions
                showRejectedAction={filters.status === 'active'}
                hiringRole={hiringRole}
              />
            )}
          </Box>
        </Box>
        <DataTable.Table selectable={isReviewerRole ? undefined : () => true} noTopRadius />
        <OutletWithDataTable />
      </DataTable.Provider>
    </Box>
  )
})

function OutletWithDataTable() {
  const dataTable = useDataTable<ApplicationsDataTableQuery>()

  return <Outlet context={{ applications: dataTable.data }} />
}
